import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { SsoUser } from '../user/sso-user';
import { map, Observable, of, switchMap } from 'rxjs';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';
import { stripAuthCallbackParams } from '../auth/auth-callback';
import { CurrentUser } from '../user/current-user';
import { CognitoAuth } from '../auth/cognito-auth';
import { SsoAuth } from '../auth/sso-auth';
import { safeReturnUrl } from '@shared/utils/routing';

export const authGuard: CanActivateFn = (route, state) => {
  const ssoAuthService = inject(SsoAuth);
  const ssoUserService = inject(SsoUser);
  const userService = inject(CurrentUser);
  const cognitoAuthService = inject(CognitoAuth);
  const router = inject(Router);

  const resolvePostAuthTarget = (): boolean | UrlTree => {
    const redirectUrl = sessionStorage.getItem(STORAGE_KEYS.AUTH_REDIRECT_URL);
    if (redirectUrl) {
      sessionStorage.removeItem(STORAGE_KEYS.AUTH_REDIRECT_URL);
      // Stripping avoids replaying a spent code; `safeReturnUrl` keeps the target inside the app.
      return router.parseUrl(safeReturnUrl(stripAuthCallbackParams(redirectUrl)));
    }
    return true;
  };

  const runSsoFlow = (): Observable<boolean | UrlTree> => {
    // Surface Auth0 error callbacks instead of redirecting again, or authorize→error loops forever.
    if (route.queryParams['error']) {
      const errorCode: string = route.queryParams['error'];
      const errorDesc: string = route.queryParams['error_description'] ?? '';
      console.error('[authGuard] Auth0 returned an error:', errorCode, errorDesc);
      return of(
        router.createUrlTree(['/auth-error'], {
          queryParams: { error: errorCode, error_description: errorDesc },
        }),
      );
    }

    return ssoAuthService.checkSession().pipe(
      switchMap((loginResponse) => {
        if (loginResponse.isAuthenticated) {
          const userData = loginResponse.userData;
          userService.isEquipment.set(false);
          userService.isGuest.set(false);
          return !ssoUserService.userLoaded() ? ssoUserService.syncUserSession(userData).pipe(map(resolvePostAuthTarget)) : of(resolvePostAuthTarget());
        }
        // Cancel the navigation so the retry redirect already under way wins.
        if (ssoAuthService.retryFailedCallback()) {
          return of(false);
        }
        sessionStorage.setItem(STORAGE_KEYS.AUTH_REDIRECT_URL, stripAuthCallbackParams(state.url));
        return of(router.createUrlTree(['/welcome']));
      }),
    );
  };

  // Equipment and guests use Cognito and must never trigger the SSO authorize() redirect.
  if (userService.usesCognito()) {
    return cognitoAuthService.isLoggedIn().pipe(
      switchMap((isLoggedIn) => {
        // Don't consume STORAGE_KEYS.AUTH_REDIRECT_URL here: a stale SSO value would hijack a guest heading to the live.
        if (isLoggedIn) return of(true);
        // A stale Cognito flag without a session falls back to the SSO flow instead of blocking.
        userService.isEquipment.set(false);
        userService.isGuest.set(false);
        return runSsoFlow();
      }),
    );
  }

  return runSsoFlow();
};
